import { type UseEnhancedFormProps, useEnhancedForm } from "@offstreetparking/design-system/forms";
import { classNames } from "@offstreetparking/design-system/utils";
import { useEffect, useRef } from "react";
import type { FieldErrors, FieldValues, UseFormReturn } from "react-hook-form";
import { FormProvider } from "react-hook-form";
import type * as z from "zod/v4";
import { FormErrorAlert } from "../Alert";

export type FormProps<TFieldValues extends FieldValues> = Omit<
	UseEnhancedFormProps<TFieldValues>,
	"onSubmit" | "initialValues" | "schema" | "t"
> & {
	children: ((methods: UseFormReturn<TFieldValues, object>) => React.ReactNode) | React.ReactNode;
	id?: string;
	noValidate?: boolean;
	initialValues?: UseEnhancedFormProps<TFieldValues>["initialValues"];
	onSubmit: (values: TFieldValues, methods: UseFormReturn<TFieldValues, object>) => void;
	onInvalid?: (errors: FieldErrors<TFieldValues>, methods: UseFormReturn<TFieldValues, object>) => void;
	className?: string;
	schema: z.ZodType<TFieldValues>;
};

export function Form<TFieldValues extends FieldValues>({
	children,
	id,
	initialValues,
	mode = "onBlur",
	noValidate = true,
	onSubmit,
	onInvalid,
	schema,
	serverError,
	className,
}: FormProps<TFieldValues>) {
	const errorRef = useRef<HTMLDivElement>(null);
	const methods = useEnhancedForm({
		initialValues,
		mode,
		schema,
		serverError,
	});

	useEffect(() => {
		if (serverError && errorRef.current) {
			errorRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [serverError]);

	return (
		<FormProvider {...methods}>
			<form
				noValidate={noValidate}
				id={id}
				onSubmit={methods.handleSubmit(
					(values) => onSubmit(values, methods),
					onInvalid ? (errors) => onInvalid(errors, methods) : undefined,
				)}
				className={classNames("space-y-6", className)}
			>
				{serverError && (
					<div ref={errorRef}>
						<FormErrorAlert serverError={serverError} />
					</div>
				)}
				{typeof children === "function" ? children(methods) : children}
			</form>
		</FormProvider>
	);
}
